import type { NetMessage, PeerId } from './transport';
import type { PlayerLoadoutSnapshot } from '../services/LoadoutSourceService';
import { sanitizeLoadoutSnapshot } from '../services/LoadoutSourceService';
import { netService } from '../services/NetService';
import { dungeonService } from '../services/DungeonService';
import { useCoopRunStore } from '../store/coopRunSlice';
import { reconnectRegistry } from './reconnect';

export interface PlayerLoadoutMsg extends NetMessage {
  type: 'player-loadout';
  snapshot: PlayerLoadoutSnapshot;
  token: string;
}
export interface RunStartMsg extends NetMessage {
  type: 'run-start';
}
export const RUN_START: RunStartMsg = { type: 'run-start' };
export function makePlayerLoadout(
  snapshot: PlayerLoadoutSnapshot,
  token: string,
): PlayerLoadoutMsg {
  return { type: 'player-loadout', snapshot, token };
}

const LOADOUT_BARRIER_TIMEOUT_MS = 8000;

/**
 * Host barrier: holds the run frozen (phase 'loading') until every party member
 * at launch has submitted its loadout, then spawns remotes, starts the sim and
 * broadcasts run-start. A safety timeout starts the run without missing peers.
 */
export class LoadoutGate {
  private expected = new Set<PeerId>();
  private received = new Set<PeerId>();
  private offMsg: () => void;
  private offLeave: () => void;
  private timeoutId: number;
  private started = false;

  constructor(expectedPeers: PeerId[], timeoutMs = LOADOUT_BARRIER_TIMEOUT_MS) {
    expectedPeers.forEach((p) => this.expected.add(p));
    const session = netService.session!;
    this.offMsg = session.on('player-loadout', (from, m) => {
      const msg = m as PlayerLoadoutMsg;
      const playerId = reconnectRegistry.resolvePlayerId(from);
      reconnectRegistry.registerToken(msg.token, playerId);
      const snap = sanitizeLoadoutSnapshot(msg.snapshot);
      dungeonService.remoteLoadouts.set(playerId, snap);
      const existing = dungeonService
        .getPlayers()
        .find((p) => p.id === playerId);
      if (existing) {
        existing.slots = snap.slots.map((s) => ({
          spellId: s.spellId,
          minorId: s.minorId,
        }));
        existing.loadoutSource = snap;
        existing.remote = true;
        existing.masteryLevels = snap.mastery ?? {};
        dungeonService.buildPlayerRunEffects(existing);
      } else {
        dungeonService.spawnRemotePlayer(playerId);
        this.received.add(playerId);
        this.checkComplete();
      }
    });
    this.offLeave = session.onPeerLeave((id) => {
      this.expected.delete(id);
      this.checkComplete();
    });
    this.timeoutId = window.setTimeout(() => this.start(), timeoutMs);
    this.checkComplete();
  }

  private checkComplete(): void {
    for (const p of this.expected) {
      if (!this.received.has(p)) {
        return;
      }
    }
    this.start();
  }

  private start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    window.clearTimeout(this.timeoutId);
    const missing = [...this.expected].filter((p) => !this.received.has(p));
    if (missing.length > 0 && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[net] loadout barrier started without loadout from: ${missing.join(', ')}`,
      );
    }
    netService.session?.broadcast('reliable', RUN_START);
    useCoopRunStore.getState().setPhase('running');
  }

  dispose(): void {
    this.offMsg();
    this.offLeave();
    window.clearTimeout(this.timeoutId);
  }
}
